import { useParams, useNavigate } from "react-router-dom";
import { MessageInput } from "./components/MessageInput";
import Card from "components/card";
import { Message } from "./types";
import { ChatHeader } from "./components/ChatHeader";
import { ChatContent } from "./components/ChatContent";
import { Agent } from "types";
import { useChatState } from "./hooks/useChatState";
import { useChatHandlers } from "./hooks/useChatHandlers";

interface ChatProps {
  open: boolean;
  onClose: () => void;
  fetchConversations?: () => void;
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  messages: Message[];
  currentAgent: Agent;
}

const Chat: React.FC<ChatProps> = ({
  open,
  onClose,
  fetchConversations,
  setMessages,
  messages,
  currentAgent,
}) => {
  const navigate = useNavigate();
  const { conversationId, agentId } = useParams<{
    conversationId?: string;
    agentId: string;
  }>();

  // Use custom hooks for state and handlers
  const chatState = useChatState({ setMessages, messages });
  const chatHandlers = useChatHandlers({
    ...chatState,
    fetchConversations,
    conversationId,
    agentId,
    navigate,
  });

  return (
    <Card extra="w-full h-[75vh] lg:h-full p-4 !font-sans relative">
      <ChatHeader
        open={open}
        currentAgent={currentAgent}
        onClose={onClose}
        isLoading={chatState.isLoading}
        onRetry={chatHandlers.retryLoadInitialMessages}
        conversationId={conversationId}
        agentId={agentId}
      />
      <ChatContent
        messagesContainerRef={chatState.messagesContainerRef}
        topSentinelRef={chatState.topSentinelRef}
        bottomSentinelRef={chatState.bottomSentinelRef}
        handleScroll={chatHandlers.handleScroll}
        isLoading={chatState.isLoading}
        isLoadingOlder={chatState.isLoadingOlder}
        isLoadingNewer={chatState.isLoadingNewer}
        loadError={chatState.loadError}
        olderError={chatState.olderError}
        newerError={chatState.newerError}
        messages={messages}
        retryLoadInitialMessages={chatHandlers.retryLoadInitialMessages}
        retryLoadOlderMessages={chatHandlers.retryLoadOlderMessages}
        retryLoadNewerMessages={chatHandlers.retryLoadNewerMessages}
        isAtBottom={chatState.isAtBottom}
        scrollToBottom={chatHandlers.scrollToBottom}
      />
      <MessageInput
        scrollToBottom={chatHandlers.scrollToBottom}
        inputMessage={chatState.inputMessage}
        isSending={chatState.isSending}
        sendMessage={chatHandlers.sendMessage}
        setInputMessage={chatState.setInputMessage}
        currentAgent={currentAgent}
      />
    </Card>
  );
};

export default Chat;
